import { useTranslation } from 'react-i18next';
import { AccordionDetails, Grid, Link, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import moment from 'moment';
import { Accordion, AccordionSummary, AccordionSummaryVariant } from '../Accordion';
import { EditOrderPanels } from '../../EditOrderTypes';
import {
  AccountData,
  PaymentEntityType,
  RefundablePaymentData,
  RefundOwnerType,
  VoucherOut,
  VoucherRedemptionData,
  VoucherService,
} from '../../../../../api';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { getDateFormat } from '../../../../../common/getDateAndTimeFormat';
import { customerFacingPriceFormatter } from '../../../../../utils/utils';
import { CalendarView, DATE_FORMAT } from '../../../../calendarWrapper/CalendarWrapper';
import { useCalendar } from '../../../../CalendarContext/CalendarContext';
import { PaymentRefundRow } from '../../../../../refunds/PaymentRefundRow';
import { RefundDialog } from '../../../../../refunds/RefundDialog';
import { findRefundItem, useRefundOverview } from '../../../../../refunds/useRefundOverview';

type VoucherRedemptionsPanelProps = {
  expanded?: EditOrderPanels;
  handleChange: any;
  voucher: VoucherOut;
};

export const VoucherRedemptionsPanel = ({ expanded, handleChange, voucher }: VoucherRedemptionsPanelProps) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const { closeEditOrderDrawer } = useCalendar();
  const account = authState.account as unknown as AccountData;
  const redemptions = voucher.redemptions ?? [];
  const { overview, mutate } = useRefundOverview(RefundOwnerType.VOUCHER, voucher.id);
  const [refundItem, setRefundItem] = useState<RefundablePaymentData | undefined>(undefined);
  const [refreshedVoucher, setRefreshedVoucher] = useState<VoucherOut | undefined>(undefined);
  const currentVoucher = refreshedVoucher?.id === voucher.id ? refreshedVoucher : voucher;
  const balanceSummary = t('vouchers.balanceOfAmount', {
    balance: customerFacingPriceFormatter(currentVoucher.balance ?? 0, account),
    amount: customerFacingPriceFormatter(voucher.price, account),
  });

  const calendarLink = (redemption: VoucherRedemptionData) => {
    const calendarView = localStorage.getItem('webook-calendarview');
    const startOfUnit =
      calendarView === CalendarView.THREE_DAY ||
      calendarView === CalendarView.AREAS ||
      calendarView === CalendarView.DAY
        ? 'day'
        : calendarView;
    // @ts-ignore
    const periodStart = moment(redemption.order_date).clone().startOf(startOfUnit);
    return `/dashboard/calendar/${periodStart.format(DATE_FORMAT)}?entityid=${
      redemption.order_parent_id || redemption.order_id
    }`;
  };

  const redemptionLabel = (redemption: VoucherRedemptionData) =>
    redemption.order_number
      ? t('vouchers.redemptions.entityWithNumber', {
          entity: t(`vouchers.redemptions.entity.${redemption.entity_type}`),
          orderNumber: redemption.order_number,
        })
      : t(`vouchers.redemptions.entity.${redemption.entity_type}`);

  return (
    <Accordion
      onChange={handleChange}
      expandedPanelName={expanded}
      panelName={EditOrderPanels.VoucherRedemptionsPanel}
      allOpenValue={EditOrderPanels.AllOpen}>
      <AccordionSummary
        variant={AccordionSummaryVariant.CAPTION}
        title={t('vouchers.redemptions.title')}
        summaryComponent={<Typography>{balanceSummary}</Typography>}
      />
      <AccordionDetails>
        <Grid container flexDirection="column" gap={1}>
          <PaymentRefundRow
            item={findRefundItem(overview, PaymentEntityType.VOUCHER, voucher.id)}
            onRefund={setRefundItem}
          />
          {redemptions.length === 0 && (
            <Typography color="text.secondary" variant="body2">
              {t('vouchers.redemptions.empty')}
            </Typography>
          )}
          {redemptions.map((redemption) => (
            <Grid container key={redemption.id} gap={1} justifyContent="space-between" flexWrap="nowrap">
              <Typography variant="body2">
                {moment(redemption.created_at).format(getDateFormat(account).longDateFormat)}
              </Typography>
              {redemption.order_id && redemption.order_date ? (
                <Link
                  component={RouterLink}
                  to={calendarLink(redemption)}
                  variant="body2"
                  onClick={closeEditOrderDrawer}>
                  {redemptionLabel(redemption)}
                </Link>
              ) : (
                <Typography variant="body2">{redemptionLabel(redemption)}</Typography>
              )}
              <Typography variant="body2" dir="ltr">
                {customerFacingPriceFormatter(redemption.amount, account)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(`vouchers.redemptions.status.${redemption.status}`)}
              </Typography>
            </Grid>
          ))}
        </Grid>
        <RefundDialog
          isOpen={!!refundItem}
          onClose={() => setRefundItem(undefined)}
          item={refundItem}
          ownerLabel={t('refunds.dialog.voucherTitle', { number: voucher.order_number })}
          voucherBalance={currentVoucher.balance}
          onRefunded={async () => {
            void mutate();
            const refreshed = await VoucherService.getVoucherById(voucher.id).catch(() => undefined);
            if (refreshed) {
              setRefreshedVoucher(refreshed);
            }
          }}
        />
      </AccordionDetails>
    </Accordion>
  );
};
